/**
 * CDD Records Details - Type Definitions
 * Complete type system for Customer Due Diligence records
 */

// ============================================================
// BASE TYPES
// ============================================================

/**
 * Entity types supported by the CDD system
 */
export type EntityType =
  | 'individual'
  | 'company'
  | 'trust'
  | 'association'
  | 'registered-body'
  | 'government-body'
  | 'partnership'
  | 'sole-trader'
  | 'foreign-company';

/**
 * Risk levels for CDD assessment
 */
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

/**
 * Verification status for identity checks
 */
export type VerificationStatus = 'verified' | 'pending' | 'failed' | 'expired' | 'not-started';

/**
 * Document verification status
 */
export type DocumentStatus = 'verified' | 'pending' | 'rejected' | 'expired';

/**
 * Screening match status
 */
export type ScreeningMatchStatus = 'clear' | 'potential-match' | 'confirmed-match' | 'false-positive';

/**
 * CDD record status
 */
export type CDDStatus =
  | 'active'
  | 'completed'
  | 'inactive'
  | 'pending-review'
  | 'in-progress'
  | 'pending'
  | 'not-started'
  | 'failed'
  | 'expired'
  | 'archived';

// ============================================================
// ADDRESS TYPES
// ============================================================

export interface Address {
  line1: string;
  line2?: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type?: 'residential' | 'business' | 'registered' | 'postal';
}

// ============================================================
// CONTACT TYPES
// ============================================================

export interface ContactDetails {
  email?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
}

// ============================================================
// IDENTITY DOCUMENT TYPES
// ============================================================

export interface IdentityDocument {
  id: string;
  type: 'passport' | 'drivers-licence' | 'medicare' | 'birth-certificate' | 'citizenship' | 'other';
  documentNumber: string;
  issuingCountry?: string;
  issuingState?: string;
  issueDate?: string;
  expiryDate?: string;
  status: DocumentStatus;
  verifiedDate?: string;
  verifiedBy?: string;
  documentUrl?: string;
}

// ============================================================
// INDIVIDUAL TYPES
// ============================================================

export interface Individual {
  id: string;
  title?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: string;
  placeOfBirth?: string;
  nationality?: string;
  residentialAddress?: Address;
  contactDetails?: ContactDetails;
  occupation?: string;
  identityDocuments?: IdentityDocument[];
  verificationStatus: VerificationStatus;
  riskLevel?: RiskLevel;
}

// ============================================================
// ASSOCIATED PERSON TYPES
// ============================================================

export type AssociatedPersonRole =
  | 'director'
  | 'beneficial-owner'
  /**
   * AL-519 item 7: the UBO role on an entity type that does not have beneficial
   * owners in the ASIC sense. Same underlying role, accurate name.
   */
  | 'senior-managing-official'
  /**
   * AL-519 item 1: split out of the former shared 'authorised-representative' id,
   * which is retired. These are unrelated — one is approval to USE the service
   * after onboarding, the other is who COMPLETED the onboarding form.
   *
   * Neither means "authorised to act for the entity". That is a property of the
   * person, carried on `authorisedToAct` / `authorityBasis` below, not a role.
   */
  | 'approved-user'
  | 'application-contact'
  | 'shareholder'
  | 'secretary'
  | 'trustee'
  | 'beneficiary'
  | 'settlor'
  | 'appointer'
  | 'nominee'
  | 'authorised-signatory'
  | 'partner'
  | 'committee-member'
  | 'office-bearer'
  | 'key-controller'
  | 'other';

export interface AssociatedPerson extends Individual {
  roles: AssociatedPersonRole[];
  /** Evidence labels retained per mapped role for compliance-facing display. */
  roleEvidence?: Partial<Record<AssociatedPersonRole, string[]>>;

  /**
   * AL-519 items 2 and 3 — authority to act for the entity.
   *
   * These are derived in `mapPersonToAssociatedPerson` rather than read in the
   * components, because `roles` above is a list of mapped STRING IDS. The raw
   * role objects the server sends — carrying `isC2User`, `isAuthorising` and
   * `status` — are collapsed through a Set and discarded, so a component has no
   * way to re-derive any of this. Anything needed on screen has to be lifted out
   * while the raw roles are still in scope.
   *
   * Authority is a property of the PERSON, not a role. It is kept off `roles`
   * deliberately so it cannot compete with Director and Beneficial Owner, which
   * are offices.
   */

  /**
   * This person has authority to act for the entity.
   *
   * Written by the server only on the C2 User. **Absent means not authorised** —
   * it is never sent as `false`, because for everyone else on the case the
   * attribute is meaningless rather than untrue.
   */
  authorisedToAct?: boolean;
  /** They hold the office themselves — authority in their own right. */
  holdsOwnOffice?: boolean;
  /** The single external principal who personally attested the C2 User may act. */
  isAuthorisingPrincipal?: boolean;
  /**
   * How the authority is established. `undefined` when `authorisedToAct` is not
   * true, since the basis of a non-existent authority is not a question.
   *
   * ⚠️ `'unknown'` means the authority is confirmed but its basis is NOT
   * established, and it must render as the bare fact with no basis stated. It
   * must never be shown as `'delegated'`. The authorisation gate can be set by
   * ASIC matching the person as a director just as readily as by an attestation,
   * so the absence of a self-claim row is missing information, not evidence of
   * delegation. Asserting delegation anyway is AL-445, which produced a report
   * telling a customer he was authorised by an external principal when there was
   * none and he was a director in his own right.
   */
  authorityBasis?: 'own-right' | 'delegated' | 'unknown';
  /** Name of the Authorising Principal. Set only when basis is `'delegated'`. */
  authorisedByName?: string;
  /**
   * A single ownership figure, present only when it is unambiguous — one source, or two that agree.
   * Undefined when the register and the beneficial-ownership declaration give DIFFERENT figures, because
   * no single number says that honestly. Read `ownership` for those; do not fall back to either value.
   */
  ownershipPercentage?: number;
  /**
   * AL-521: what each source says about this person's holding, never totalled.
   *
   * `registeredPercentage` is the ASIC share register as SimpleKYC returned it; `declaredPercentage` is
   * our own beneficial-ownership determination. They are usually the same holding described twice, which
   * is why the previous single `totalOwnershipPercentage` added them and reported a 50% owner as 100%.
   */
  ownership?: {
    registeredPercentage: number | null;
    declaredPercentage: number | null;
    sourcesAgree: boolean;
  };
  votingRights?: number;
  appointmentDate?: string;
  cessationDate?: string;
  isPEP?: boolean;
  pepDetails?: string;
  isSanctioned?: boolean;
  sanctionDetails?: string;
}

// ============================================================
// COMPANY TYPES
// ============================================================

export interface CompanyRegistration {
  abn?: string;
  acn?: string;
  arbn?: string;
  registrationDate?: string;
  registrationState?: string;
  companyType?: string;
  companyClass?: string;
  companySubclass?: string;
}

export interface CompanyProfile {
  id: string;
  entityType: 'company';
  legalName: string;
  tradingName?: string;
  registration: CompanyRegistration;
  registeredAddress: Address;
  principalPlaceOfBusiness?: Address;
  contactDetails?: ContactDetails;
  industryCode?: string;
  industryDescription?: string;
  dateOfIncorporation?: string;
  status: 'registered' | 'deregistered' | 'under-external-administration';
  asicStatus?: string;
}

export interface ShareholderStructure {
  shareholders: {
    name: string;
    type: 'individual' | 'company' | 'trust' | 'other';
    sharesHeld: number;
    shareClass: string;
    percentageOwnership: number;
    beneficialOwner?: boolean;
  }[];
  totalShares: number;
  shareClasses: {
    className: string;
    totalShares: number;
    votingRights: boolean;
  }[];
}

export interface CompanyStructure {
  shareholderStructure?: ShareholderStructure;
  parentCompany?: {
    name: string;
    abn?: string;
    acn?: string;
    country?: string;
    ownershipPercentage?: number;
  };
  subsidiaries?: {
    name: string;
    abn?: string;
    acn?: string;
    country?: string;
    ownershipPercentage?: number;
  }[];
}

// ============================================================
// TRUST TYPES
// ============================================================

export type TrustType =
  | 'discretionary'
  | 'unit'
  | 'fixed'
  | 'hybrid'
  | 'bare'
  | 'charitable'
  | 'testamentary'
  | 'superannuation'
  | 'managed-investment'
  | 'other';

export interface TrustRegistration {
  abn?: string;
  tfn?: string;
  trustDeedDate?: string;
  trustDeedAmendmentDate?: string;
  vestingDate?: string;
}

export interface TrustProfile {
  id: string;
  entityType: 'trust';
  trustName: string;
  trustType: TrustType;
  registration: TrustRegistration;
  trusteeType: 'individual' | 'corporate';
  businessAddress?: Address;
  contactDetails?: ContactDetails;
  settlorDetails?: string;
  appointerDetails?: string;
  establishmentDate?: string;
  status: 'active' | 'vested' | 'terminated';
}

export interface TrustRoles {
  trustees: AssociatedPerson[];
  beneficiaries: AssociatedPerson[];
  settlors: AssociatedPerson[];
  appointers?: AssociatedPerson[];
  nominees?: AssociatedPerson[];
}

// ============================================================
// ASSOCIATION TYPES
// ============================================================

export interface AssociationRegistration {
  abn?: string;
  incorporationNumber?: string;
  registrationState?: string;
  registrationDate?: string;
}

export interface AssociationProfile {
  id: string;
  entityType: 'association';
  name: string;
  registration: AssociationRegistration;
  registeredAddress: Address;
  contactDetails?: ContactDetails;
  purpose?: string;
  rules?: string;
  financialYear?: string;
  status: 'registered' | 'deregistered' | 'under-administration';
}

export interface AssociationCommittee {
  members: AssociatedPerson[];
  positions: {
    position: string;
    personId: string;
    appointmentDate?: string;
  }[];
}

// ============================================================
// REGISTERED BODY TYPES (AFSL, RSE etc.)
// ============================================================

export interface RegulatoryLicence {
  licenceType: 'AFSL' | 'ACL' | 'RSE' | 'ARSN' | 'OTHER';
  licenceNumber: string;
  licenceHolderName: string;
  issueDate?: string;
  expiryDate?: string;
  status: 'current' | 'suspended' | 'cancelled' | 'expired';
  conditions?: string[];
  authorisations?: string[];
}

export interface RegisteredBodyProfile {
  id: string;
  entityType: 'registered-body';
  name: string;
  registration: {
    abn?: string;
    acn?: string;
    arbn?: string;
    registrationDate?: string;
  };
  registeredAddress: Address;
  contactDetails?: ContactDetails;
  governingLegislation?: string;
  regulatoryBody?: string;
  regulatoryLicences: RegulatoryLicence[];
  status: 'active' | 'inactive';
}

// ============================================================
// SCREENING TYPES
// ============================================================

export interface ScreeningResult {
  id: string;
  screeningType: 'PEP' | 'sanctions' | 'adverse-media' | 'criminal' | 'comprehensive';
  screeningDate: string;
  screenedEntity: string;
  provider?: string;
  status: ScreeningMatchStatus;
  matches?: ScreeningMatch[];
  nextReviewDate?: string;
  reviewedBy?: string;
  reviewedDate?: string;
  notes?: string;
}

export interface ScreeningMatch {
  id: string;
  matchType: 'exact' | 'close' | 'partial' | 'PEP' | 'sanctions' | 'adverse-media';
  matchScore: number;
  matchedName: string;
  matchedList?: string;
  matchedCountry?: string;
  matchDetails?: string;
  details?: string;
  status?: ScreeningMatchStatus;
  resolution?: 'confirmed' | 'false-positive' | 'pending';
  resolvedBy?: string;
  resolvedDate?: string;
  resolutionNotes?: string;
}

// ============================================================
// CUSTOMER ASSESSMENT TYPES
// ============================================================

export interface RiskAssessmentAnswer {
  questionId: string;
  questionText: string;
  answer: string; // "Yes" | "No" | "Unanswered"
}

export interface RiskAssessment {
  id: string;
  /**
   * AL-437: optional. The CDD report sets `assessedAt` to null, so this is normally
   * absent. It must never be defaulted — the previous fallback chain ended at
   * `new Date()`, giving a compliance artefact an assessment date that changed on
   * every page load.
   */
  assessmentDate?: string;
  /**
   * AL-437: optional. The report does not emit `assessedBy`. Defaulting it asserted
   * that "ARCaml Risk Engine" performed an assessment, on every record.
   */
  assessedBy?: string;
  overallRiskRating: RiskLevel;
  customerRiskFactors: {
    factor: string;
    rating: RiskLevel;
    notes?: string;
  }[];
  /**
   * AL-437: optional, and normally absent. `CddReportAssembler.assembleRiskAssessment`
   * emits no `factors` block — confirmed in both the platform and c1-services copies —
   * so these carry a value only if that changes. They previously defaulted to 'low',
   * which presented four affirmative Low findings where there was no finding at all.
   *
   * `deliveryChannelRisk` was removed rather than made optional: it was a hardcoded
   * literal that consulted no data source, so there was nothing to render
   * conditionally.
   */
  productServiceRisk?: RiskLevel;
  geographicRisk?: RiskLevel;
  industryRisk?: RiskLevel;
  pepRisk?: RiskLevel;
  transactionRisk?: RiskLevel;
  mitigatingFactors?: string[];
  enhancedDueDiligenceRequired: boolean;
  eddMeasures?: string[];
  nextReviewDate: string;
  approvedBy?: string;
  approvalDate?: string;
  notes?: string;
  answers?: RiskAssessmentAnswer[];
}

export interface CustomerAssessment {
  riskAssessments: RiskAssessment[];
  currentRiskRating: RiskLevel;
  customerCategory?: string;
  customerSegment?: string;
  relationshipStartDate?: string;
  productsServices?: string[];
  expectedActivity?: {
    transactionTypes?: string[];
    expectedVolume?: string;
    expectedValue?: string;
    countriesInvolved?: string[];
  };
  sourceOfWealth?: string;
  sourceOfFunds?: string;
}

// ============================================================
// CHECKS LOG TYPES
// ============================================================

export type CheckType =
  | 'identity-verification'
  | 'document-verification'
  | 'company-verification'
  | 'trust-verification'
  | 'association-verification'
  | 'entity-verification'
  | 'sanctions-screening'
  | 'pep-screening'
  | 'adverse-media'
  | 'risk-assessment'
  | 'enhanced-due-diligence'
  | 'periodic-review'
  | 'transaction-monitoring'
  | 'source-of-funds'
  | 'beneficial-ownership'
  | 'other';

export interface CDDCheck {
  id: string;
  checkType: CheckType;
  checkDate: string;
  performedBy: string;
  status: 'completed' | 'in-progress' | 'failed' | 'pending';
  result?: 'pass' | 'fail' | 'review-required' | 'inconclusive' | 'pending';
  details?: string;
  /**
   * AL-482: who or what the check was performed on. Sent to C1 deliberately — without it a
   * multi-person case shows several identity checks with no way to tell them apart. Optional
   * because a payload from a server predating AL-482 will not carry it.
   */
  subject?: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  documentReferences?: string[];
  expiryDate?: string;
  notes?: string;
  dataSource?: string;
  scope?: string;
}

export interface ChecksLog {
  checks: CDDCheck[];
  lastCheckDate?: string;
  nextScheduledCheck?: string;
  complianceStatus: 'compliant' | 'non-compliant' | 'review-required';
  outstandingChecks?: {
    checkType: CheckType;
    dueDate: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
  }[];
}

// ============================================================
// AUDIT TRAIL TYPES
// ============================================================

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'view'
  | 'approve'
  | 'reject'
  | 'submit'
  | 'archive'
  | 'restore'
  | 'export'
  | 'verify'
  | 'review';

export interface AuditEntry {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  action: AuditAction;
  entityType: string;
  entityId: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  notes?: string;
}

export interface AuditTrail {
  entries: AuditEntry[];
  createdDate: string;
  createdBy: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

// ============================================================
// MAIN CDD RECORD TYPE
// ============================================================

export interface CDDRecordBase {
  id: string;
  referenceNumber: string;
  entityType: EntityType;
  status: CDDStatus;
  riskLevel: RiskLevel;
  verificationStatus: VerificationStatus;
  createdDate: string;
  createdBy: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
  assignedTo?: string;
  tags?: string[];
  notes?: string;

  /**
   * The customer's own declaration of the nature and purpose of the business
   * relationship (AL-438).
   *
   * Collected by the C2 onboarding form and shown on the OPS Case Management
   * page, but never surfaced to the C1 — which is the wrong way round, since the
   * C1 is the reporting entity relying on this CDD and collection is required by
   * AML Rules 6-1(3), 6-2(4), 6-3(6) and 6-4(3).
   *
   * Lives on CDDRecordBase rather than CustomerAssessment deliberately. The
   * transform only builds customerAssessment when the report carries a
   * riskAssessment block, so a field placed there would disappear for any case
   * without one. It also applies to every entity type, so putting it on the
   * per-type profiles would mean repeating it four times.
   *
   * Legitimately absent: not required of INDIVIDUAL cases and not asked of child
   * workflows, so roughly three quarters of live rows are null. Absent must
   * render as nothing, not as an empty label.
   */
  natureOfBusiness?: string;

  // Nested data
  associatedPersons: AssociatedPerson[];
  screeningResults: ScreeningResult[];
  customerAssessment?: CustomerAssessment;
  checksLog: ChecksLog;
  auditTrail: AuditTrail;
}

// Company CDD Record
export interface CompanyCDDRecord extends CDDRecordBase {
  entityType: 'company';
  profile: CompanyProfile;
  structure?: CompanyStructure;
}

// Trust CDD Record
export interface TrustCDDRecord extends CDDRecordBase {
  entityType: 'trust';
  profile: TrustProfile;
  roles?: TrustRoles;
}

// Association CDD Record
export interface AssociationCDDRecord extends CDDRecordBase {
  entityType: 'association';
  profile: AssociationProfile;
  committee?: AssociationCommittee;
}

// Registered Body CDD Record
export interface RegisteredBodyCDDRecord extends CDDRecordBase {
  entityType: 'registered-body';
  profile: RegisteredBodyProfile;
}

// Individual CDD Record
export interface IndividualCDDRecord extends CDDRecordBase {
  entityType: 'individual';
  profile: Individual;
}

// Union type for all CDD records
export type CDDRecord =
  | CompanyCDDRecord
  | TrustCDDRecord
  | AssociationCDDRecord
  | RegisteredBodyCDDRecord
  | IndividualCDDRecord;

// ============================================================
// COMPONENT PROPS TYPES
// ============================================================

export interface CDDRecordDetailsProps {
  record: CDDRecord;
  onEdit?: (record: CDDRecord) => void;
  onBack?: () => void;
  onExport?: (format: 'pdf' | 'csv' | 'json') => void;
}

export interface CDDHeaderProps {
  record: CDDRecord;
  onBack?: () => void;
  onExport?: (format: 'pdf' | 'csv' | 'json') => void;
  onEdit?: () => void;
}

export interface CDDNavigationProps {
  activeSection: string;
  sections: { id: string; label: string; count?: number }[];
  onSectionChange: (sectionId: string) => void;
}

export interface AssociatedPersonsProps {
  persons: AssociatedPerson[];
  title?: string;
  onPersonClick?: (person: AssociatedPerson) => void;
}

export interface IdentityVerificationProps {
  person: AssociatedPerson;
  hideHeader?: boolean;
}

export interface ScreeningProps {
  results: ScreeningResult[];
  onResolveMatch?: (matchId: string, resolution: ScreeningMatch['resolution']) => void;
  onInitiateScreening?: () => void;
  recordVerificationStatus?: VerificationStatus;
}

export interface CustomerAssessmentProps {
  assessment?: CustomerAssessment;
  /**
   * AL-438: passed separately rather than read off `assessment`, because it is
   * recorded on the case rather than on a risk assessment and must survive a
   * case that has no assessment block.
   */
  natureOfBusiness?: string;
  onUpdateRisk?: (newRating: RiskLevel) => void;
  onAddAssessment?: () => void;
  recordVerificationStatus?: VerificationStatus;
}

export interface ChecksLogProps {
  checksLog: ChecksLog;
  onAddCheck?: () => void;
  onViewCheck?: (checkId: string) => void;
  recordVerificationStatus?: VerificationStatus;
}

export interface AuditTrailProps {
  auditTrail: AuditTrail;
  onExport?: () => void;
}

// Company-specific props
export interface CompanySummaryProps {
  profile: CompanyProfile;
  riskLevel: RiskLevel;
  verificationStatus: VerificationStatus;
}

export interface CompanyProfileProps {
  profile: CompanyProfile;
  onEdit?: () => void;
}

export interface StructureControlProps {
  structure?: CompanyStructure;
  associatedPersons: AssociatedPerson[];
  onPersonClick?: (person: AssociatedPerson) => void;
  /**
   * AL-519 item 7: needed to name the ownership column correctly.
   *
   * This component lives under `sections/company/` but is rendered for every
   * entity type, so it cannot assume "Beneficial Owners" is the right heading.
   * Optional, and absence falls back to "Beneficial Owners" — the behaviour
   * before this ticket.
   */
  entityType?: EntityType | string;
}

// Trust-specific props
export interface TrustProfileProps {
  profile: TrustProfile;
  onEdit?: () => void;
}

export interface TrustRolesMapProps {
  roles?: TrustRoles;
  onPersonClick?: (person: AssociatedPerson) => void;
}

// Association-specific props
export interface AssociationProfileProps {
  profile: AssociationProfile;
  committee?: AssociationCommittee;
  onEdit?: () => void;
}

// Registered body props
export interface RegulatorModuleProps {
  licences: RegulatoryLicence[];
}

// ============================================================
// UTILITY TYPES
// ============================================================

export type SectionId =
  | 'summary'
  | 'profile'
  | 'structure'
  | 'roles'
  | 'committee'
  | 'associated-persons'
  | 'identity-verification'
  | 'screening'
  | 'assessment'
  | 'checks'
  | 'audit'
  | 'licences'
  | 'documents'
  // AL-417: who on this case has not acted yet.
  | 'case-progress';

export interface Section {
  id: SectionId;
  label: string;
  count?: number;
  visible: boolean;
}

export function getSectionsForEntityType(entityType: EntityType): Section[] {
  const commonSections: Section[] = [
    { id: 'summary', label: 'Summary', visible: true },
    { id: 'profile', label: 'Profile', visible: true },
    // AL-417: deliberately a normal section rather than only living in the in-progress card. That card
    // is gated on status === 'in-progress', but normalizeCDDStatus defaults to 'pending' and most cases
    // resolve to it — so a panel that only appeared there would be invisible for the majority of cases,
    // which are exactly the ones with people still to act.
    { id: 'case-progress', label: 'Case Progress', visible: true },
    // AL-519 item 5: was 'Authorised Representatives', which named one disputed
    // role rather than the population the section actually lists — and never
    // rendered in full in the left nav at any width ("Authorised Repres…").
    // The section's own tiles already said "Associated Persons".
    { id: 'associated-persons', label: 'Associated Persons', visible: true },
    { id: 'screening', label: 'Screening', visible: true },
    { id: 'assessment', label: 'Customer Assessment', visible: true },
    { id: 'checks', label: 'Checks Log', visible: true },
    { id: 'audit', label: 'Audit Trail', visible: false },
  ];

  switch (entityType) {
    case 'company':
      return [
        ...commonSections.slice(0, 2),
        { id: 'structure', label: 'Key Personnel', visible: true },
        ...commonSections.slice(2),
      ];
    case 'trust':
      return [
        ...commonSections.slice(0, 2),
        { id: 'roles', label: 'Trust Roles', visible: true },
        ...commonSections.slice(2),
      ];
    case 'association':
      return [
        ...commonSections.slice(0, 2),
        { id: 'committee', label: 'Committee', visible: true },
        ...commonSections.slice(2),
      ];
    case 'registered-body':
      return [
        ...commonSections.slice(0, 2),
        { id: 'licences', label: 'Regulatory Licences', visible: true },
        ...commonSections.slice(2),
      ];
    default:
      return commonSections;
  }
}

// Helper type guards
export function isCompanyCDDRecord(record: CDDRecord): record is CompanyCDDRecord {
  return record.entityType === 'company';
}

export function isTrustCDDRecord(record: CDDRecord): record is TrustCDDRecord {
  return record.entityType === 'trust';
}

export function isAssociationCDDRecord(record: CDDRecord): record is AssociationCDDRecord {
  return record.entityType === 'association';
}

export function isRegisteredBodyCDDRecord(record: CDDRecord): record is RegisteredBodyCDDRecord {
  return record.entityType === 'registered-body';
}

export function isIndividualCDDRecord(record: CDDRecord): record is IndividualCDDRecord {
  return record.entityType === 'individual';
}
